import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { SignalRNotificationModel } from '../models/signalr-notification.model';
import { environment } from 'environments/environment';
import { NotificationSearchModel } from '../models';
import { COMMON_SERVICE, CommonInterface } from './common.interface';

@Injectable({ providedIn: 'root' })
export class SignalRNotificationService implements OnDestroy {

  //#region Fields
  searchModel: NotificationSearchModel = new NotificationSearchModel();

  private hubConnection: signalR.HubConnection | null = null;

  private readonly HUB_URL = `${environment.apiurl}notificationHub`;

  private _token: string = '';
  private _startPromise: Promise<void> | null = null;
  private _pendingStoreId: number | null = null;

  private _notifications$ = new BehaviorSubject<SignalRNotificationModel[]>([]);
  private _unreadCount$ = new BehaviorSubject<number>(0);

  // Bell sound should play only for new realtime notifications.
  private readonly NOTIFICATION_SOUND_PATH = 'assets/notification/Short-clear-bell-ding-sound-effect.mp3';
  private _notificationAudio: HTMLAudioElement | null = null;

  private _playNotificationSound(): void {
    try {
      if (typeof Audio === 'undefined') {
        return;
      }

      if (!this._notificationAudio) {
        this._notificationAudio = new Audio(this._resolveNotificationSoundUrl());
        this._notificationAudio.preload = 'auto';
      }

      this._notificationAudio.currentTime = 0;
      const playPromise = this._notificationAudio.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          // Ignore autoplay-block or missing-file errors to avoid breaking notification flow.
        });
      }
    } catch {
      // Sound should never block notification delivery.
    }
  }

  private _resolveNotificationSoundUrl(): string {
    try {
      return new URL(this.NOTIFICATION_SOUND_PATH, this._document.baseURI).toString();
    } catch {
      return this.NOTIFICATION_SOUND_PATH;
    }
  }

  //#endregion

  //#region Properties

  notifications$ = this._notifications$.asObservable();
  unreadCount$ = this._unreadCount$.asObservable();

  //#endregion

  //#region Constructor

  constructor(
    @Inject(COMMON_SERVICE) private _commonService: CommonInterface,
    @Inject(DOCUMENT) private _document: Document,
  ) { }

  //#endregion

  //#region Connection

  startConnection(token?: string): void {
    this._token = token ?? '';

    if (this.hubConnection?.state === signalR.HubConnectionState.Connected || this._startPromise) {
      return;
    }

    this._loadNotificationsFromApi(false);

    if (!this.hubConnection) {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(this.HUB_URL, {
          accessTokenFactory: () => this._token,
          transport: signalR.HttpTransportType.LongPolling,
          headers: {
            Authorization: `Bearer ${this._token}`
          }
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this._registerHandlers();
    }

    this._startPromise = this.hubConnection
      .start()
      .then(() => {
        console.log('[SignalR] Connected');
        this._startPromise = null;
      })
      .catch(err => {
        console.error('[SignalR] Connection error:', err);
        this._startPromise = null;
      });
  }

  stopConnection(): void {
    this._pendingStoreId = null;
    this._startPromise = null;
    this.hubConnection?.stop();
    this.hubConnection = null;
  }

  //#endregion

  //#region Private Methods

  private _loadNotificationsFromApi(mergeWithCurrent: boolean): void {
    this._commonService.getNotificationList(this.searchModel).subscribe({
      next: (res) => {
        if (res?.status !== 200 || !res.data) return;

        const notifications = this._normalizeApiNotifications(res);
        const updated = mergeWithCurrent
          ? this._mergeNotifications(this._notifications$.getValue(), notifications)
          : notifications;

        this._setNotifications(updated);
        console.log('[SignalR] Loaded from API:', notifications.length, 'notifications');
      },
      error: (err) => console.error('[SignalR] API load failed:', err)
    });
  }

  private _registerHandlers(): void {
    this.hubConnection?.on('ReceiveNotification', (data: SignalRNotificationModel) => {
      console.log('[SignalR] ReceiveNotification:', data);
      const incoming = this._normalizeNotification(data, false);
      const current = this._notifications$.getValue();

      if (current.some(notification => notification.id === incoming.id)) {
        return;
      }

      this._setNotifications([incoming, ...current]);
      this._playNotificationSound();
    });

    this.hubConnection?.onreconnected(() => {
      console.log('[SignalR] Reconnected — reloading from API');
      this._loadNotificationsFromApi(false);
    });

    this.hubConnection?.onreconnecting(() => console.warn('[SignalR] Reconnecting...'));
    this.hubConnection?.onclose(err => console.warn('[SignalR] Closed', err));
  }

  private _updateUnreadCount(notifications: SignalRNotificationModel[]): void {
    this._unreadCount$.next(
      notifications.filter(n => !n.isRead).length
    );
  }

  markAsRead(notificationId: number): void {
    this._commonService.notificationMarkAsRead(notificationId).subscribe({
      next: () => {
        const updated = this._notifications$.getValue().map(notification => (
          notification.id === notificationId ? { ...notification, isRead: true } : notification
        ));
        this._setNotifications(updated);
        console.log('[SignalR] Marked as read:', notificationId);
      },
      error: (err) => console.error('[SignalR] MarkAsRead failed:', err)
    });
  }

  reloadNotifications(searchmodel?: NotificationSearchModel): void {
    this.searchModel = { ...this.searchModel, ...searchmodel };
    this.searchModel.page = 1;
    this._loadNotificationsFromApi(false);
  }

  loadMoreNotifications(searchmodel?: NotificationSearchModel): void {
    this.searchModel = { ...this.searchModel, ...searchmodel };
    this.searchModel.page = (this.searchModel.page ?? 1) + 1;
    this._loadNotificationsFromApi(true);
  }

  clearAll(): void {
    this._setNotifications([]);
  }

  // private _normalizeApiNotifications(res: any): SignalRNotificationModel[] {
  //   return this._commonService.changeFormatUtcToIst(res).map((notification: SignalRNotificationModel) =>
  //     this._normalizeNotification(notification)
  //   );
  // }

  private _normalizeApiNotifications(res: any): SignalRNotificationModel[] {
    return (res.data || []).map((notification: SignalRNotificationModel) =>
      this._normalizeNotification(notification)
    );
  }

  private _normalizeNotification(notification: SignalRNotificationModel, preserveReadState = true): SignalRNotificationModel {
    return {
      ...notification,
      isRead: preserveReadState ? notification.isRead ?? false : false,
      timestamp: this._resolveTimestamp(notification)
    };
  }

  private _resolveTimestamp(notification: Pick<SignalRNotificationModel, 'createdOn' | 'validFrom'>): Date {
    const source = notification.createdOn ?? notification.validFrom;
    return source ? new Date(source) : new Date();
  }

  private _mergeNotifications(
    current: SignalRNotificationModel[],
    incoming: SignalRNotificationModel[]
  ): SignalRNotificationModel[] {
    const mergedById = new Map<number, SignalRNotificationModel>();

    [...current, ...incoming].forEach(notification => {
      mergedById.set(notification.id, notification);
    });

    return Array.from(mergedById.values());
  }

  private _setNotifications(notifications: SignalRNotificationModel[]): void {
    this._notifications$.next(notifications);
    this._updateUnreadCount(notifications);
  }

  private _flushPendingStoreUpdate(): void {
    if (this._pendingStoreId == null) {
      return;
    }

    const storeId = this._pendingStoreId;
    this._pendingStoreId = null;
  }

  ngOnDestroy(): void {
    this.stopConnection();
  }
  //#endregion

}


